import React, { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Camera, Upload, RotateCcw, ZoomIn, ScanLine,
  Plus, Trash2, Check
} from "lucide-react";
import {
  detectDocumentCorners, applyFilter, SCAN_MODES
} from "./scannerService";
import { invoiceLog } from "./invoiceLogger";

// ─── Corner handle overlay (SVG-based for crisp rendering) ───────────────────
function CornerOverlay({ corners, imgSize, dragging, onDown }) {
  if (!corners || !imgSize.w) return null;
  const { w, h } = imgSize;

  const cornerLabels = ["TL", "TR", "BR", "BL"];
  const edgeColor = "#22c55e";

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${w} ${h}`}
      style={{ touchAction: "none" }}
    >
      {/* Dark mask outside quad */}
      <defs>
        <mask id="docmask">
          <rect width={w} height={h} fill="white" />
          <polygon
            points={corners.map(c => c.join(",")).join(" ")}
            fill="black"
          />
        </mask>
      </defs>
      <rect width={w} height={h} fill="rgba(0,0,0,0.55)" mask="url(#docmask)" />

      {/* Quad border */}
      <polygon
        points={corners.map(c => c.join(",")).join(" ")}
        fill="none"
        stroke={edgeColor}
        strokeWidth="2"
        strokeDasharray="6,4"
      />

      {/* Edge midpoint guides */}
      {corners.map((c, i) => {
        const next = corners[(i + 1) % 4];
        const mx = (c[0] + next[0]) / 2, my = (c[1] + next[1]) / 2;
        return <circle key={`mid-${i}`} cx={mx} cy={my} r="4" fill={edgeColor} opacity="0.5" />;
      })}

      {/* Corner handles — pointer-events enabled */}
      {corners.map((c, i) => (
        <g
          key={`cat-${i}`}
          style={{ pointerEvents: "all", cursor: "grab" }}
          onMouseDown={e => onDown(e, i)}
          onTouchStart={e => onDown(e, i)}
        >
          <circle cx={c[0]} cy={c[1]} r="22" fill="transparent" />
          <circle
            cx={c[0]} cy={c[1]} r={dragging === i ? 14 : 11}
            fill={dragging === i ? "#16a34a" : edgeColor}
            stroke="white" strokeWidth="2.5"
          />
          <text
            x={c[0]} y={c[1] + 4}
            textAnchor="middle" fontSize="8"
            fill="white" fontWeight="bold"
          >{cornerLabels[i]}</text>
        </g>
      ))}
    </svg>
  );
}

// ─── Page thumbnail strip ─────────────────────────────────────────────────────
function PageStrip({ pages, currentIdx, onSelect, onRemove, onAdd, fileInputRef }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {pages.map((p, i) => (
        <div
          key={`proc-${i}`}
          className={`relative flex-shrink-0 cursor-pointer rounded-lg border-2 transition-all ${
            i === currentIdx ? "border-[#7B2D3F] shadow-md" : "border-gray-200 hover:border-gray-400"
          }`}
          onClick={() => onSelect(i)}
        >
          <img src={p.originalUrl} alt={`Pàgina ${i + 1}`} className="w-14 h-20 object-cover rounded-md" />
          <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#7B2D3F] text-white text-[10px] font-bold flex items-center justify-center">
            {i + 1}
          </div>
          {pages.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); onRemove(i); }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
            >
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={() => fileInputRef.current?.click()}
        className="flex-shrink-0 w-14 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-[#7B2D3F] hover:text-[#7B2D3F] transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="text-[9px] mt-1">Pàgina</span>
      </button>
    </div>
  );
}

// ─── Scan mode selector ───────────────────────────────────────────────────────
function ScanModeSelector({ value, onChange, compact = false }) {
  return (
    <div className={`flex ${compact ? "flex-row gap-1.5 flex-wrap" : "flex-col gap-2"}`}>
      {!compact && (
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Mode d'escàner</p>
      )}
      {SCAN_MODES.map(m => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
            value === m.id
              ? "bg-[#7B2D3F] text-white border-[#7B2D3F] shadow-sm"
              : "bg-white text-gray-700 border-gray-200 hover:border-[#7B2D3F]/50"
          }`}
        >
          <span className="text-base leading-none">{m.icon}</span>
          <div className="text-left">
            <p className="text-xs font-semibold leading-tight">{m.label}</p>
            {!compact && <p className="text-[10px] opacity-70 leading-tight mt-0.5">{m.desc}</p>}
          </div>
          {value === m.id && <Check className="w-3 h-3 ml-auto" />}
        </button>
      ))}
    </div>
  );
}

// ─── Quality indicator ────────────────────────────────────────────────────────
function QualityIndicator({ imageData, w, h }) {
  const [quality, setQuality] = useState(null);

  useEffect(() => {
    if (!imageData || !w || !h) return;
    // Estimate sharpness via Laplacian variance
    const d = imageData.data;
    const gray = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      gray[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
    }
    let variance = 0, count = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const lap = Math.abs(
          -gray[(y-1)*w+x] - gray[(y+1)*w+x] - gray[y*w+(x-1)] - gray[y*w+(x+1)] + 4*gray[y*w+x]
        );
        variance += lap; count++;
      }
    }
    const score = Math.min(100, Math.round((variance / count) * 3));
    setQuality(score);
  }, [imageData, w, h]);

  if (quality === null) return null;
  const color = quality >= 60 ? "text-emerald-600 bg-emerald-50 border-emerald-200"
    : quality >= 30 ? "text-amber-600 bg-amber-50 border-amber-200"
    : "text-red-600 bg-red-50 border-red-200";
  const label = quality >= 60 ? "Bona qualitat" : quality >= 30 ? "Qualitat acceptable" : "Qualitat baixa";

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-semibold ${color}`}>
      <div className="w-1.5 h-1.5 rounded-full bg-current" />
      {label} ({quality}/100)
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DocumentScanner({ onProcessed, onCancel }) {
  const fileInputRef = useRef(null);
  const canvasRef    = useRef(null);
  const imgRef       = useRef(null);

  const [step, setStep] = useState("upload"); // upload | adjust | preview
  const [pages, setPages] = useState([]);        // [{originalFile, originalUrl, imgEl, corners, imgSize, processedUrl, scanMode}]
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [dragging, setDragging] = useState(null);
  const [scanMode, setScanMode] = useState("grayscale");
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageData, setImageData] = useState(null);

  const currentPage = pages[currentPageIdx];

  // ─── Load image ─────────────────────────────────────────────────────────────
  const loadImageFile = useCallback((file) => {
    const stop = invoiceLog.startTimer("loadImage");
    invoiceLog.info("scanner", `Loading file: ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const maxW = Math.min(img.width, 900);
      const scale = maxW / img.width;
      const w = Math.round(maxW), h = Math.round(img.height * scale);

      // Render to tmp canvas for pixel data
      const tmpCanvas = document.createElement("canvas");
      tmpCanvas.width = w; tmpCanvas.height = h;
      const tmpCtx = tmpCanvas.getContext("2d");
      tmpCtx.drawImage(img, 0, 0, w, h);
      const imgData = tmpCtx.getImageData(0, 0, w, h);
      const corners = detectDocumentCorners(imgData, w, h);
      invoiceLog.info("scanner", "Corners detected", corners);

      const page = { originalFile: file, originalUrl: url, imgEl: img, corners, imgSize: { w, h }, processedUrl: null, scanMode };
      setPages(prev => {
        const next = [...prev, page];
        setCurrentPageIdx(next.length - 1);
        return next;
      });
      setImageData(imgData);
      imgRef.current = img;
      stop();
      setStep("adjust");
    };
    img.src = url;
  }, [scanMode]);

  const handleFileChange = (e) => {
    Array.from(e.target.files || []).forEach(loadImageFile);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    Array.from(e.dataTransfer.files).forEach(loadImageFile);
  };

  // ─── Page switching ──────────────────────────────────────────────────────────
  const switchPage = (idx) => {
    if (!pages[idx]) return;
    setCurrentPageIdx(idx);
    imgRef.current = pages[idx].imgEl;
    setImageData(null); // re-evaluate on draw
    setScanMode(pages[idx].scanMode || "grayscale");
  };

  const removePage = (idx) => {
    setPages(prev => {
      const next = prev.filter((_, i) => i !== idx);
      setCurrentPageIdx(Math.min(idx, next.length - 1));
      return next;
    });
  };

  // ─── Update corner for current page ─────────────────────────────────────────
  const setCorners = (corners) => {
    setPages(prev => {
      const next = [...prev];
      next[currentPageIdx] = { ...next[currentPageIdx], corners };
      return next;
    });
  };

  // ─── Draw overlay canvas ────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "adjust" || !canvasRef.current || !currentPage?.imgEl) return;
    const { imgEl, imgSize } = currentPage;
    const canvas = canvasRef.current;
    canvas.width = imgSize.w;
    canvas.height = imgSize.h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgEl, 0, 0, imgSize.w, imgSize.h);
  }, [step, currentPageIdx, pages]);

  // ─── Drag interaction ────────────────────────────────────────────────────────
  const getRelativePos = (e, el) => {
    const rect = el.getBoundingClientRect();
    const scaleX = (currentPage?.imgSize.w || 1) / rect.width;
    const scaleY = (currentPage?.imgSize.h || 1) / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
  };

  const handleCornerDown = (e, i) => {
    e.preventDefault();
    setDragging(i);
  };

  useEffect(() => {
    if (dragging === null) return;
    const handleMove = (e) => {
      e.preventDefault();
      if (!canvasRef.current || !currentPage) return;
      const pos = getRelativePos(e, canvasRef.current);
      const { w, h } = currentPage.imgSize;
      const newCorners = [...currentPage.corners];
      newCorners[dragging] = [Math.max(0, Math.min(w, pos[0])), Math.max(0, Math.min(h, pos[1]))];
      setCorners(newCorners);
    };
    const handleUp = () => setDragging(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [dragging, currentPage]);

  // ─── Process all pages ───────────────────────────────────────────────────────
  const processAllPages = async () => {
    setIsProcessing(true);
    const stop = invoiceLog.startTimer("processAllPages");
    try {
      const processed = await Promise.all(pages.map(async (page, idx) => {
        const mode = page.scanMode || scanMode;
        invoiceLog.info("scanner", `Processing page ${idx + 1}`, { mode });

        // Read pixel data from the already-drawn canvas (canvasRef for current page, or re-draw)
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = page.imgSize.w;
        srcCanvas.height = page.imgSize.h;
        const ctx = srcCanvas.getContext("2d", { willReadFrequently: true });

        // Draw from blob URL (same-origin, no taint)
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => { ctx.drawImage(img, 0, 0, srcCanvas.width, srcCanvas.height); resolve(); };
          img.onerror = reject;
          img.src = page.originalUrl; // blob URL — always same-origin
        });

        applyFilter(ctx, srcCanvas.width, srcCanvas.height, mode);

        const dataUrl = srcCanvas.toDataURL("image/jpeg", 0.92);
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], `page_${idx + 1}.jpg`, { type: "image/jpeg" });

        return { ...page, processedUrl: dataUrl, processedFile: file };
      }));

      setPages(processed);
      stop();
      setStep("preview");
    } catch (e) {
      invoiceLog.error("scanner", "Processing failed", { error: e.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const updatePageMode = (mode) => {
    setScanMode(mode);
    setPages(prev => {
      const next = [...prev];
      if (next[currentPageIdx]) next[currentPageIdx] = { ...next[currentPageIdx], scanMode: mode };
      return next;
    });
  };

  const handleConfirm = () => {
    invoiceLog.info("scanner", "User confirmed scan", { pages: pages.length });
    // Primary page = first page for OCR
    onProcessed({
      pages,
      originalFile: pages[0].originalFile,
      processedFile: pages[0].processedFile,
      processedDataUrl: pages[0].processedUrl,
      scanMode: pages[0].scanMode || scanMode,
    });
  };

  // ─── UPLOAD STEP ─────────────────────────────────────────────────────────────
  if (step === "upload") {
    return (
      <div className="flex flex-col items-center gap-6 min-h-[420px] justify-center">
        <div className="w-16 h-16 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center">
          <ScanLine className="w-8 h-8 text-[#7B2D3F]" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-900">Escanejar factura</h3>
          <p className="text-sm text-gray-500 mt-1">Selecciona una imatge o fes una foto amb la càmera</p>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          className="w-full max-w-sm border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center gap-3 cursor-pointer hover:border-[#7B2D3F]/50 hover:bg-[#7B2D3F]/5 transition-all group"
        >
          <Upload className="w-8 h-8 text-gray-400 group-hover:text-[#7B2D3F] transition-colors" />
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">Arrossega aquí o fes clic</p>
            <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WEBP — múltiples pàgines suportades</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-2"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.setAttribute("capture", "environment");
                fileInputRef.current.click();
              }
            }}
          >
            <Camera className="w-4 h-4" /> Fer foto
          </Button>
          <Button variant="outline" onClick={onCancel}>Cancel·lar</Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    );
  }

  // ─── ADJUST STEP ─────────────────────────────────────────────────────────────
  if (step === "adjust") {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Ajusta els cantons</h3>
            <p className="text-xs text-gray-500">Arrossega els punts verds als 4 cantons de la factura</p>
          </div>
          {imageData && <QualityIndicator imageData={imageData} w={currentPage?.imgSize.w} h={currentPage?.imgSize.h} />}
        </div>

        {/* Multi-page strip */}
        {pages.length > 0 && (
          <PageStrip
            pages={pages}
            currentIdx={currentPageIdx}
            onSelect={switchPage}
            onRemove={removePage}
            onAdd={() => fileInputRef.current?.click()}
            fileInputRef={fileInputRef}
          />
        )}

        {/* Canvas with SVG overlay */}
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-900 select-none" style={{ touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            style={{ width: "100%", display: "block" }}
          />
          {currentPage && (
            <CornerOverlay
              corners={currentPage.corners}
              imgSize={currentPage.imgSize}
              dragging={dragging}
              onDown={handleCornerDown}
            />
          )}
        </div>

        {/* Scan mode */}
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Mode d'escàner</p>
          <ScanModeSelector value={scanMode} onChange={updatePageMode} compact />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setStep("upload"); setPages([]); }} className="gap-2">
            <RotateCcw className="w-4 h-4" /> Nova imatge
          </Button>
          <Button
            className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white flex-1 gap-2"
            onClick={processAllPages}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Processant…</>
            ) : (
              <><ZoomIn className="w-4 h-4" /> Processar i continuar</>
            )}
          </Button>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
      </div>
    );
  }

  // ─── PREVIEW STEP ────────────────────────────────────────────────────────────
  if (step === "preview") {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="font-bold text-gray-900">Previsualització — {pages.length} pàgina{pages.length > 1 ? "s" : ""}</h3>
          <p className="text-xs text-gray-500">Comprova el resultat. Pots reajustar si cal.</p>
        </div>

        {/* Page comparisons */}
        <div className="space-y-4">
          {pages.map((page, i) => (
            <div key={`page-${i}`} className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">Original P{i+1}</p>
                <img src={page.originalUrl} alt="Original" className="w-full rounded-xl border border-gray-200 object-contain max-h-52 bg-gray-50" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-bold text-emerald-600 uppercase">Processat P{i+1}</p>
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                    {SCAN_MODES.find(m => m.id === (page.scanMode || scanMode))?.label}
                  </Badge>
                </div>
                <img src={page.processedUrl} alt="Processat" className="w-full rounded-xl border border-emerald-200 object-contain max-h-52 bg-white" />
              </div>
            </div>
          ))}
        </div>

        {/* Mode change hint */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <p className="text-xs text-blue-700">
            💡 Si el text no es llegeix bé, torna enrere i prova <strong>Alt contrast</strong> o <strong>B/N net</strong>
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setStep("adjust")} className="gap-2">
            <RotateCcw className="w-4 h-4" /> Reajustar
          </Button>
          <Button className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white flex-1 gap-2" onClick={handleConfirm}>
            <ScanLine className="w-4 h-4" /> Extreure dades amb IA
          </Button>
        </div>
      </div>
    );
  }

  return null;
}